import { Button } from '@ctw/shared/components/button';
import { Card } from '@ctw/shared/components/containers/card';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { Link } from '@ctw/shared/components/link';
import { notify, notifyFromModal } from '@ctw/shared/components/toast';
import { PatientSummarizationUsageTicker } from '@ctw/shared/content/summarization/patient-summarization-usage-ticker';
import {
  PatientSummarizationLimitApproachingBanner,
  PatientSummarizationLimitReachedBanner,
} from '@ctw/shared/content/summarization/patient-summarization-warning-banners';
import { usePatientSummaryJob } from '@ctw/shared/content/summarization/patient-summary-job-provider';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS } from '@ctw/shared/context/telemetry/timer-metrics';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useInterval } from '@ctw/shared/hooks/use-interval';
import { formatDateToLocalHumanDateString } from '@ctw/shared/utils/dates';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { siblingPathname } from '@ctw/shared/utils/url';
import { faMessageDots } from '@fortawesome/pro-duotone-svg-icons';
import {
  faArrowTrendUp,
  faArrowUpRightFromSquare,
  faCheckCircle,
  faCircleCheck,
  faEnvelope,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays } from 'date-fns/addDays';
import { endOfMonth } from 'date-fns/endOfMonth';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Returns a month-scoped UI flag key so the banner reappears each month. */
export const getUiFlagLimitApproachingBannerHidden = () => {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return `patient-summarization-limit-approaching-banner-hidden-${month}`;
};

/** Returns a month-scoped UI flag key so the banner reappears each month. */
export const getUiFlagLimitReachedBannerHidden = () => {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return `patient-summarization-limit-reached-banner-hidden-${month}`;
};

export type PatientSummarizationCardState =
  | 'loading'
  | 'generating'
  | 'success'
  | 'limit_not_reached'
  | 'limit_approaching'
  | 'limit_reached';

interface DeterminePatientSummarizationCardStateArgs {
  userPatientSummarizationState: ReturnType<typeof useUserPatientSummarizationContext>;
  patientSummarizationResult: ReturnType<typeof usePatientSummaries> | null;
  isCheckingCache?: boolean;
}

const determinePatientSummarizationCardState = ({
  userPatientSummarizationState,
  patientSummarizationResult,
  isCheckingCache,
}: DeterminePatientSummarizationCardStateArgs): PatientSummarizationCardState => {
  if (isCheckingCache) {
    return 'loading';
  }

  if (patientSummarizationResult?.status === 'insufficient-data') {
    // Short circuit - a job has been run, but there's not enough data.
    // Dedicated handling of this state happens on the GPS page.
    // For the overview card, just go back to the pre-submission state.
    return 'limit_not_reached';
  }

  const { summarizationLimitReached, summarizationLimitApproaching } =
    userPatientSummarizationState;
  const summarizationJobExists =
    patientSummarizationResult !== null && patientSummarizationResult.status !== 'error';
  const summarizationJobCompleted = patientSummarizationResult?.status === 'completed';
  const summarizationJobRunning = patientSummarizationResult?.status === 'running';

  if (summarizationJobRunning) {
    return 'generating';
  }

  if (summarizationJobCompleted) {
    return 'success';
  }

  if (!(summarizationJobExists || summarizationLimitReached || summarizationLimitApproaching)) {
    return 'limit_not_reached';
  }

  if (!summarizationJobExists && summarizationLimitApproaching) {
    return 'limit_approaching';
  }

  return 'limit_reached';
};

export interface PatientSummarizationStatusCardProps {
  patientSummarizationResult: ReturnType<typeof usePatientSummaries> | null;
  onGenerateSummary: () => void;
  isCheckingCache?: boolean;
  hideUsageTicker?: boolean;
  upsell?: boolean;
}

export const PatientSummarizationStatusCard = ({
  patientSummarizationResult,
  onGenerateSummary,
  isCheckingCache,
  hideUsageTicker = false,
  upsell = true,
}: PatientSummarizationStatusCardProps) => {
  const userPatientSummarizationState = useUserPatientSummarizationContext();

  const patientSummarizationCardState = determinePatientSummarizationCardState({
    userPatientSummarizationState,
    patientSummarizationResult,
    isCheckingCache,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [animatedHeight, setAnimatedHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setAnimatedHeight(el.scrollHeight);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Card
      testId="patient-summarization-status-card"
      fullbleed={true}
      className={twx('@container bg-background-main')}
    >
      <div
        className={tw`overflow-hidden transition-[height] duration-300 ease-out`}
        style={{ height: animatedHeight === undefined ? 'auto' : `${animatedHeight}px` }}
      >
        <div ref={contentRef}>
          {patientSummarizationCardState === 'loading' && <PatientSummarizationLoading />}
          {patientSummarizationCardState === 'generating' && (
            <PatientSummarizationGeneratingSummary upsell={upsell} />
          )}
          {patientSummarizationCardState === 'success' && <PatientSummarizationSuccess />}
          {patientSummarizationCardState === 'limit_not_reached' && (
            <PatientSummarizationLimitNotReached
              generateSummary={onGenerateSummary}
              hideUsageTicker={hideUsageTicker}
            />
          )}
          {patientSummarizationCardState === 'limit_approaching' && (
            <PatientSummarizationLimitApproaching
              generateSummary={onGenerateSummary}
              hideUsageTicker={hideUsageTicker}
              upsell={upsell}
            />
          )}
          {patientSummarizationCardState === 'limit_reached' &&
            (upsell ? (
              <PatientSummarizationLimitUpsell hideUsageTicker={hideUsageTicker} />
            ) : (
              <PatientSummarizationLimitReached />
            ))}
        </div>
      </div>
    </Card>
  );
};

const PatientSummarizationLoading = () => {
  return (
    <div className={tw`relative`}>
      {/* Invisible instance of the default card state to reserve the correct height */}
      <div className={tw`invisible`} aria-hidden="true">
        <PatientSummarizationLimitNotReached
          generateSummary={() => {
            /* noop */
          }}
        />
      </div>
      <div className={tw`absolute inset-0 flex items-center gap-3 p-4`}>
        <FontAwesomeIcon
          icon={faSparkles}
          className={tw`h-5 w-5 pt-0.5 text-primary-main`}
          beatFade={true}
          style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
        />
        <div className={tw`text-content-subtle text-sm`}>Checking for patient summary...</div>
      </div>
    </div>
  );
};

const LOADING_BAR_TICK_INTERVAL_MS = 150 as const;
const LOADING_BAR_WIDTH_STEP = 0.5 as const;

const PatientSummarizationGeneratingSummary = ({ upsell }: { upsell?: boolean }) => {
  const { summarizationLimitApproaching } = useUserPatientSummarizationContext();
  const [loadingBarTickIntervalMilliseconds, setLoadingBarTickIntervalMilliseconds] = useState<
    number | null
  >(LOADING_BAR_TICK_INTERVAL_MS);
  const [loadingBarWidth, setLoadingBarWidth] = useState(0);

  useInterval({
    intervalInMilliseconds: loadingBarTickIntervalMilliseconds,
    intervalFunction: () => {
      setLoadingBarWidth((previousWidth) => {
        const progressedWidth = Math.min(previousWidth + LOADING_BAR_WIDTH_STEP, 100);

        if (progressedWidth >= 100) {
          setLoadingBarTickIntervalMilliseconds(null);
        }

        return progressedWidth;
      });
    },
  });

  return (
    <div>
      <div className={tw`flex @3xl:flex-row flex-col justify-between gap-3 p-4`}>
        <div className={tw`flex min-w-0 flex-1 items-center justify-start gap-3`}>
          <FontAwesomeIcon
            icon={faSparkles}
            className={tw`h-5 w-5 shrink-0 pt-0.5 text-primary-main`}
            beatFade={true}
            style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
          />
          <div>
            <div className={tw`text-content-main`}>Generating patient summary...</div>
            <div className={tw`text-content-subtle text-sm`}>Loading patient chart entries...</div>
          </div>
        </div>
        <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
          <Button
            type="button"
            variant="primary"
            className={tw`cursor-wait hover:bg-primary-main active:scale-100`}
          >
            <div className={tw`flex items-center space-x-2`}>
              <div className={tw`whitespace-nowrap text-sm`}>Generating summary</div>
              <FontAwesomeIcon
                icon={faMessageDots}
                beatFade={true}
                style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
                className={tw`h-4 w-4`}
              />
            </div>
          </Button>
        </div>
      </div>
      <div
        className={tw`h-1 animate-gentle-pulse bg-logo-green/85 transition-[width] ease-linear`}
        style={{
          width: `${loadingBarWidth}%`,
          transitionDuration: `${LOADING_BAR_TICK_INTERVAL_MS}ms`,
        }}
      />
      {summarizationLimitApproaching && (
        <PatientSummarizationLimitApproachingBanner upsell={upsell} />
      )}
    </div>
  );
};

const PatientSummarizationSuccess = () => {
  const { navigate, pathname } = useZuiContext();

  const navigateToSummary = useCallback(() => {
    navigate(siblingPathname(pathname, 'summary'));
  }, [navigate, pathname]);

  return (
    <div>
      <div className={tw`flex @3xl:flex-row flex-col justify-between gap-3 p-4`}>
        <div className={tw`flex min-w-0 flex-1 items-center justify-start gap-3`}>
          <FontAwesomeIcon
            icon={faSparkles}
            className={tw`h-5 w-5 shrink-0 pt-0.5 text-success-main/95`}
          />
          <div className={tw`text-content-main`}>Patient Summary Ready</div>
        </div>
        <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
          <Button type="button" variant="primary" onClick={navigateToSummary}>
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faCircleCheck} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>View Patient Summary</div>
            </div>
          </Button>
        </div>
      </div>
      <div className={tw`h-1 w-full bg-success-main/95 transition-all duration-500`} />
    </div>
  );
};

interface PatientSummarizationLimitNotReachedProps {
  generateSummary: () => void;
  hideUsageTicker?: boolean;
}

const PatientSummarizationLimitNotReached = ({
  generateSummary,
  hideUsageTicker,
}: PatientSummarizationLimitNotReachedProps) => {
  const telemetry = useTelemetry();
  const { summaryResponse } = usePatientSummaryJob();

  return (
    <div className={tw`flex @3xl:flex-row flex-col justify-between gap-3 p-4`}>
      <div className={tw`flex min-w-0 flex-1 items-center justify-start gap-3`}>
        <FontAwesomeIcon
          icon={faSparkles}
          className={tw`h-5 w-5 shrink-0 pt-0.5 text-primary-main`}
        />
        <div className={tw`text-content-main`}>Generate a 5-minute briefing for this patient</div>
      </div>
      <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
        {!hideUsageTicker && <PatientSummarizationUsageTicker />}
        <Button
          type="button"
          variant="primary"
          telemetryInteractionName="patient_summary_generation_initiated"
          telemetryObfuscatedIdentifier="Generate Patient Summary"
          telemetryContext={{
            summaryJobId: summaryResponse.summaryJobId,
          }}
          onClick={() => {
            telemetry.startTimerMetric(
              'loading',
              TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS,
            );
            generateSummary();
          }}
        >
          <div className={tw`flex items-center space-x-2`}>
            <FontAwesomeIcon icon={faSparkles} className={tw`h-4 w-4`} />
            <div className={tw`whitespace-nowrap text-sm`}>Generate Patient Summary</div>
          </div>
        </Button>
      </div>
    </div>
  );
};

interface PatientSummarizationLimitApproachingProps {
  generateSummary: () => void;
  hideUsageTicker?: boolean;
  upsell?: boolean;
}

const PatientSummarizationLimitApproaching = ({
  generateSummary,
  hideUsageTicker,
  upsell,
}: PatientSummarizationLimitApproachingProps) => {
  return (
    <div className={twx('border-2 border-caution-main/15')}>
      <div className={tw`flex @3xl:flex-row flex-col justify-between gap-3 p-4`}>
        <div className={tw`flex min-w-0 flex-1 items-center justify-start gap-3`}>
          <FontAwesomeIcon
            icon={faSparkles}
            className={tw`h-5 w-5 shrink-0 pt-0.5 text-primary-main`}
          />
          <div className={tw`text-content-main`}>Generate a 5-minute briefing for this patient</div>
        </div>
        <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
          {!hideUsageTicker && <PatientSummarizationUsageTicker />}
          <Button type="button" variant="primary" onClick={() => generateSummary()}>
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faSparkles} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>Generate Patient Summary</div>
            </div>
          </Button>
        </div>
      </div>
      <PatientSummarizationLimitApproachingBanner upsell={upsell} />
    </div>
  );
};

interface PatientSummarizationLimitUpsellProps {
  hideUsageTicker?: boolean;
}

const PatientSummarizationLimitUpsell = ({
  hideUsageTicker,
}: PatientSummarizationLimitUpsellProps) => {
  const { summaryResponse } = usePatientSummaryJob();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();

  return (
    <div className={twx('border-2 border-error-main/15')}>
      <div className={tw`flex @3xl:flex-row flex-col justify-between gap-3 p-4`}>
        <div className={tw`flex min-w-0 flex-1 items-center justify-start gap-3`}>
          <FontAwesomeIcon icon={faSparkles} className={tw`h-5 w-5 shrink-0 text-error-text`} />
          <div className={tw`text-error-text`}>You've reached your monthly AI summary limit</div>
        </div>
        <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
          {!hideUsageTicker && <PatientSummarizationUsageTicker />}
          <Button
            type="button"
            variant="primary-destructive"
            onClick={openUpgradeModal}
            telemetryInteractionName="patient_summary_upgrade_modal_opened"
            telemetryObfuscatedIdentifier="Upgrade Now"
            telemetryContext={{
              summaryJobId: summaryResponse.summaryJobId,
              summaryJobStatus: summaryResponse.status,
              upgradeModalTrigger: 'upgrade-now-button',
            }}
          >
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>Upgrade Now</div>
            </div>
          </Button>
        </div>
      </div>
      <PatientSummarizationLimitReachedBanner />
    </div>
  );
};

const PatientSummarizationLimitReached = () => {
  return (
    <div className={twx('border-content-main/15 bg-background-hover')}>
      <div className={tw`flex @3xl:flex-row flex-col items-center justify-between gap-3 p-4`}>
        <FontAwesomeIcon
          icon={faCheckCircle}
          className={tw`h-5 w-5 shrink-0 text-content-subtle`}
        />
        <div className={tw`min-w-0 flex-1`}>
          <div className={tw`text-content-main`}>
            You've used all your included summaries this month
          </div>
          <div className={tw`text-content-subtle text-sm`}>
            Monthly limits will reset on{' '}
            {formatDateToLocalHumanDateString(addDays(endOfMonth(new Date()), 1))}
          </div>
        </div>
        <div className={tw`flex shrink-0 flex-wrap items-center justify-start gap-3`}>
          <Link
            href="https://clinicalguide.zushealth.com/docs/generated-patient-summary"
            className={tw`text-content-subtle text-sm`}
          >
            Learn more about GPS quotas
          </Link>
        </div>
      </div>
    </div>
  );
};

const UPGRADE_BENEFITS = [
  {
    title: 'Summaries for every patient',
    description: 'Generate clinical summaries for your entire panel without monthly caps.',
  },
  {
    title: 'Time-saving automation',
    description: 'Save hours each week by automating the synthesis of complex patient history.',
  },
  {
    title: 'Priority feature access',
    description: 'Be the first to use new beta summary features.',
  },
] as const;

interface PatientSummarizationUpgradeModalBodyProps {
  closeModal: () => void;
  summariesUsed: number;
  summariesAllotted: number;
}

const PatientSummarizationUpgradeModalBody = ({
  closeModal,
  summariesUsed,
  summariesAllotted,
}: PatientSummarizationUpgradeModalBodyProps) => {
  const { requestContext } = useCTW();
  const telemetry = useTelemetry();
  const hasReachedLimit = summariesUsed >= summariesAllotted;

  const [shouldCcAdminOrManager, setShouldCcAdminOrManager] = useState(true);
  const [adminOrManagerEmail, setAdminOrManagerEmail] = useState<string | null>(null);
  const [additionalNotes, setAdditionalNotes] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${getBffApiBaseUrl(requestContext.env)}/v1/summarization-upgrade-requests`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
            'Zus-Account': requestContext.builderId,
          },
          body: JSON.stringify({
            data: {
              type: 'upgrade-requests',
              attributes: {
                adminEmail:
                  shouldCcAdminOrManager && adminOrManagerEmail ? adminOrManagerEmail : undefined,
                notes: additionalNotes || undefined,
              },
            },
          }),
        },
      );

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(
          `Failed to submit upgrade request (${response.status} ${response.statusText})${body ? `: ${body}` : ''}`,
        );
      }

      closeModal();
      notify({ type: 'success', title: 'Upgrade request submitted' });
    } catch (error) {
      telemetry.trackError({
        message: 'Error submitting summarization upgrade request',
        error: error instanceof Error ? error : new Error(String(error)),
      });
      notifyFromModal({
        type: 'error',
        title: 'Unable to submit upgrade request. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={tw`relative flex w-[480px] max-w-full flex-col`}>
      <div
        className={tw`flex items-start gap-3 border-success-badge border-b bg-success-badge/55 p-5`}
      >
        <div
          className={tw`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white drop-shadow`}
        >
          <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-5 w-5 text-primary-main`} />
        </div>
        <div>
          <h2 className={tw`font-semibold text-lg text-success-text`}>Unlock Full AI Access</h2>
          <p className={tw`text-sm text-success-text`}>
            {hasReachedLimit
              ? `You've used all ${summariesAllotted.toLocaleString('en-US')} free summaries this month.`
              : `You've used ${summariesUsed.toLocaleString('en-US')} of ${summariesAllotted.toLocaleString('en-US')} free summaries this month.`}
          </p>
        </div>
      </div>

      <div className={tw`px-6 pt-6`}>
        <div className={tw`font-semibold text-content-main text-sm uppercase tracking-wider`}>
          When you upgrade, you get:
        </div>
        <ul className={tw`mt-3 flex flex-col gap-3`}>
          {UPGRADE_BENEFITS.map((benefit) => (
            <li key={benefit.title} className={tw`flex items-start gap-2.5`}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className={tw`mt-1 h-4 w-4 shrink-0 text-primary-main`}
              />
              <div>
                <div className={tw`font-semibold text-content-main`}>{benefit.title}</div>
                <div className={tw`text-content-subtle text-sm`}>{benefit.description}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={tw`mx-6 mt-6 flex flex-col gap-3 rounded-lg border border-border-main/50 bg-content-focal/40 p-4`}
      >
        <div>
          <Checkbox
            checked={shouldCcAdminOrManager}
            onChange={setShouldCcAdminOrManager}
            name="cc-admin-manager"
            label="CC my team admin or manager on this request"
          />
          <p className={tw`w-full pl-7 text-content-subtle text-xs leading-tight`}>
            We'll include them in the email to expedite the approval process.
          </p>
        </div>
        {shouldCcAdminOrManager && (
          <div className={tw`flex items-center justify-center gap-3 pt-1`}>
            <div className={tw`w-full`}>
              <label
                htmlFor="upgrade-admin-or-manager-email"
                className={tw`flex items-start font-semibold text-content-subtle text-xs uppercase tracking-wider`}
              >
                <div className={tw`leading-tight`}>Admin or Manager email</div>
                <div className={tw`-mt-0.5 text-error-text text-xl leading-none`}>*</div>
              </label>
              <input
                id="upgrade-admin-or-manager-email"
                type="email"
                required={true}
                inputMode="email"
                autoComplete="email"
                value={adminOrManagerEmail ?? ''}
                onChange={(event) => {
                  setAdminOrManagerEmail(
                    `${event.target.value}`.trim().length === 0 ? null : event.target.value,
                  );
                }}
                placeholder="manager@company.com"
                className={tw`w-full rounded-md border border-border-main px-3 py-2 text-content-main text-sm placeholder:text-content-subtle/50 focus:border-primary-main focus:outline-none focus:ring-1 focus:ring-primary-main`}
              />
            </div>
          </div>
        )}
      </div>
      <div className={tw`px-6 pt-4`}>
        <label
          htmlFor="upgrade-additional-notes"
          className={tw`block font-semibold text-content-subtle text-xs uppercase tracking-wider`}
        >
          Additional notes{' '}
          <span className={tw`font-normal normal-case tracking-normal`}>(optional)</span>
        </label>
        <textarea
          id="upgrade-additional-notes"
          value={additionalNotes ?? ''}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Any specific requirements or questions..."
          rows={3}
          className={tw`mt-1.5 w-full resize-none rounded-md border border-border-main px-3 py-2 text-content-main text-sm placeholder:text-content-subtle/50 focus:border-primary-main focus:outline-none focus:ring-1 focus:ring-primary-main`}
        />
      </div>

      <div className={tw`flex flex-col items-center gap-3 px-6 pt-4 pb-6`}>
        <Button
          type="button"
          variant="primary"
          className={tw`w-full justify-center`}
          disabled={isSubmitting}
          onClick={() => void handleSubmit()}
        >
          <div className={tw`flex items-center justify-center gap-2`}>
            <FontAwesomeIcon icon={faEnvelope} className={tw`h-4 w-4`} />
            <div className={tw`font-bold text-[0.9rem]`}>
              {isSubmitting ? 'Submitting...' : 'Request Upgrade'}
            </div>
          </div>
        </Button>
        <div className={tw`flex flex-col items-center gap-1`}>
          <p className={tw`text-content-subtle text-xs`}>
            Our team will reach out within 1-2 business days to process your request.
          </p>
          <Link
            target="_blank"
            href="https://clinicalguide.zushealth.com/docs/generated-patient-summary"
            className={tw`inline-flex items-center gap-2`}
          >
            <div className={tw`font-semibold text-sm`}>Learn more about AI Summary pricing</div>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={tw`h-3 w-3`} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export const usePatientSummarizationUpgradeModal = () => {
  const telemetry = useTelemetry();
  const { summariesUsed, summariesAllotted } = useUserPatientSummarizationContext();
  const { openModal, closeModal } = useModal({
    boundaryName: 'PatientSummarizationUpgradeModal',
    layout: {
      mode: 'fit',
      overflowBehavior: 'visible',
      fullbleed: true,
      showCloseButton: true,
    },
  });

  const openUpgradeModal = useCallback(
    () =>
      openModal({
        telemetry,
        action: {
          label: 'Upgrade',
          title: 'Unlock Full AI Access',
          content: {
            body: (
              <PatientSummarizationUpgradeModalBody
                closeModal={closeModal}
                summariesUsed={summariesUsed}
                summariesAllotted={summariesAllotted}
              />
            ),
          },
        },
      }),
    [telemetry, openModal, closeModal, summariesUsed, summariesAllotted],
  );

  return { openUpgradeModal, closeUpgradeModal: closeModal };
};
